/**
 * The two roots every TanStack Query key starts with, and the helpers that read
 * them. This file is the only place the roots are spelled. Convention:
 * `lib/query/keys/README.md`.
 *
 * - `['school', schoolId, '<domain>', …]`: the fetch depends on a school. The
 *   `schoolId` is the school the request is sent for.
 * - `['global', '<domain>', …]`: the fetch ignores `x-school-id` and every other
 *   school input.
 */

import type { QueryKey } from '@tanstack/react-query'

/**
 * Root of every key for one school. On its own it is the "everything for this
 * school" invalidation target, never a query key. A disabled query passes
 * `null` and gates on `enabled: !!schoolId`.
 */
export const schoolScope = (schoolId: string | null) => ['school', schoolId] as const

/** Root of every key whose fetch does not depend on a school. */
export const globalScope = ['global'] as const

/** True when the key starts at one of the two roots (built by a factory). */
export const isScopedKey = (key: QueryKey) => key[0] === 'school' || key[0] === 'global'

/** True when `key` equals `prefix` or sits under it, segment by segment. */
export const keyStartsWith = (key: QueryKey, prefix: QueryKey) =>
  prefix.length <= key.length && prefix.every((segment, i) => key[i] === segment)
